import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d63l0cibdoed7v0v8plg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-56 pb-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#1A2332] mb-6 leading-tight">
            Let's discuss your situation
          </h1>
          <p className="text-xl text-[#6B7280] leading-relaxed">
            Reach out to schedule a consultation or ask a question. We'll respond within one business day.
          </p>
        </div>
      </section>

      <section className="pt-40 pb-32 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-[#1A2332] mb-6">
              Get in touch
            </h1>
            <p className="text-xl text-[#6B7280] leading-relaxed max-w-2xl mx-auto">
              Let's discuss your legal needs. Every conversation starts with understanding your situation and exploring how we can help.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4704B] focus:border-transparent text-base"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4704B] focus:border-transparent text-base"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4704B] focus:border-transparent text-base"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4704B] focus:border-transparent text-base"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Service Interest
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4704B] focus:border-transparent text-base"
                  >
                    <option value="">Select a service</option>
                    <option value="Contract Drafting & Review">Contract Drafting &amp; Review</option>
                    <option value="Business Formation">Business Formation</option>
                    <option value="Commercial Agreements">Commercial Agreements</option>
                    <option value="Asset Protection">Asset Protection</option>
                    <option value="General Business Counsel">General Business Counsel</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Message * <span className="text-xs text-[#6B7280] font-normal">(Max 500 characters)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C4704B] focus:border-transparent text-base resize-none"
                    placeholder="Tell us about your legal needs..."
                  />
                  <p className="text-xs text-[#6B7280] mt-1 text-right">
                    {formData.message.length}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#C4704B] text-white px-8 py-4 rounded-lg text-base font-medium hover:bg-[#A85A3A] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-[#C4704B]/10 border border-[#C4704B] text-[#A85A3A] px-4 py-3 rounded-lg text-sm">
                    Thank you for reaching out. We'll get back to you shortly.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
                    Something went wrong. Please try again or email us directly.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="bg-[#F8F9FA] rounded-2xl p-10 mb-8">
                <h2 className="text-2xl font-bold text-[#1A2332] mb-6">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1">
                      <i className="ri-mail-line text-xl text-[#C4704B]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1A2332] mb-1">Email</p>
                      <a href="mailto:contact@semeikslaw.com" className="text-base text-[#6B7280] hover:text-[#C4704B] transition-colors">
                        contact@semeikslaw.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center mr-4 mt-1">
                      <i className="ri-phone-line text-xl text-[#C4704B]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1A2332] mb-1">Phone</p>
                      <a href="tel:+15188885987" className="text-base text-[#6B7280] hover:text-[#C4704B] transition-colors">
                        (518) 888-5987
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#1A2332] rounded-2xl p-10">
                <h3 className="text-xl font-bold text-white mb-4">
                  What to expect
                </h3>
                <p className="text-base text-white/90 leading-relaxed mb-4">
                  When you reach out, we'll schedule a conversation to understand your situation and discuss how we can help.
                </p>
                <p className="text-base text-white/90 leading-relaxed">
                  Initial consultations are straightforward: we listen, ask questions, and provide honest feedback about your legal needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
